import type { FormikHelpers } from "formik";
import { Formik, useFormikContext } from "formik";
import { useContext, useEffect, useState } from "react";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";
import {
	initialValues,
	validationSchema,
	t2InitialValues,
} from "./perennialCropland";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetPerennialCroplandDefaults,
	useContextAwareGetPerennialCropland,
	useUpdatePerennialCroplandMutation,
} from "@hooks/index";
import type { ModuleFull } from "./perennialCroplandTypes";
import { BelongsToLuc } from "@typedefs/modulesInterfaces";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import { shouldShowRenewal } from "./perennialCropland";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import usePerennialCroplandSchema from "./usePerennialCroplandSchema";
import ModuleContext from "../../ModuleContext";

const PerennialCropland = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { belongsToLuc } = useContext(ModuleContext);
	const { moduleList } = useAppSelector(selectCurrentBuilder);

	const id = moduleList.find(({ id }) => id === FEModules.PerennialCropland)?.uniqueId;

	const {
		data: perennialCroplandsData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetPerennialCropland(id ?? 0, { refetchOnMountOrArgChange: true, });
	const [
		updatePerennialCroplands,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdatePerennialCroplandMutation();

	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetPerennialCroplandDefaults(id ?? 0, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });

	const { moduleSchema, t2Schema } = usePerennialCroplandSchema();
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});
	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (perennialCroplandsData) {
			setInitValues({
				module: perennialCroplandsData.module,
				tiertwo: { ...perennialCroplandsData.tiertwo, ...defaults },
			});

			processModuleThreads({ module: perennialCroplandsData.module });
		}

	}, [perennialCroplandsData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const payload = {
			data: { ...values },
			activityId: activeActivityId ?? 0,
		};

		try {
			values.module.id && (await updatePerennialCroplands(payload).unwrap());
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema(belongsToLuc)}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.perennial_cropland"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					isLoading={isLoadingUpdate}
					beModuleType={BEModulesClassNames.PerennialCropland}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				>
					{({ belongsToLuc }) => (
						<HandleConditional belongsToLuc={belongsToLuc} />
					)}
				</GenericModule>
				<GenericTierTwo
					title="project.perennial_cropland"
					t2Schema={t2Schema}
					warningMessage={defaultsErrorMsg ?? null}
				/>
			</>
		</Formik>
	);
};

const HandleConditional = ({ belongsToLuc }: { belongsToLuc: BelongsToLuc | undefined }) => {
	const { values, setFieldValue } = useFormikContext<ModuleFull>();

	useEffect(() => {
		const showRenewal = shouldShowRenewal(values, belongsToLuc);
		setFieldValue("module.show_renewal", showRenewal);
		if (!showRenewal) {
			setFieldValue("module.is_complete_renewal_start", false);
			setFieldValue("module.is_complete_renewal_w", false);
			setFieldValue("module.is_complete_renewal_wo", false);
		}

		const showMaturity = !belongsToLuc || belongsToLuc.is_start;
		setFieldValue("module.show_maturity", showMaturity);
	}, [values, belongsToLuc, setFieldValue]);

	return <></>;
};

export default PerennialCropland;
